import { isDefined, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback } from 'react';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderClientWay } from '@/App/tiles/orders/components/OrderClientWay.tsx';
import { OrderFooter } from '@/App/tiles/orders/components/OrderFooter.tsx';
import { OrderHeader } from '@/App/tiles/orders/components/OrderHeader.tsx';
import { OrderNotifications } from '@/App/tiles/orders/components/OrderNotifications.tsx';
import { OrderNotional } from '@/App/tiles/orders/components/OrderNotional.tsx';
import { ConnectedFixingOrderSpot } from '@/App/tiles/orders/FixingOrder/ConnectedFixingOrderSpot.tsx';
import {
  useFixingIsChecking,
  useFixingIsModifyingField,
  useFixingSetIsModifyingField,
  useFixingTileId,
  useValidateFixing,
} from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { FixingCommercialMargin } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingCommercialMargin.tsx';
import { FixingDate } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingDate.tsx';
import { FixingDeliveryDate } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingDeliveryDate.tsx';
import { FixingMarginType } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingMarginType.tsx';
import { FixingPlace } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingPlace.tsx';
import { FixingPriceType } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingPriceType.tsx';
import { FixingReference } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingReference.tsx';
import { FixingTime } from '@/App/tiles/orders/FixingOrder/FixingOrderCreationTile/FixingTime.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useSubmitFixingMutation } from '@/store/orders/fixing/api/index.ts';
import {
  selectClientWayFixingData,
  selectFixingErrors,
  selectFixingIsStandardGroup,
  selectFixingMarginType,
  selectFixingTile,
  selectNotificationEmailsFixing,
  selectNotionalDataFixing,
} from '@/store/orders/fixing/selectors.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

/**
 * Memoized FixingOrderTile component because the Grid rerenders a lot.
 */
export const FixingOrderCreationTile = memo(() => {
  const track = useTrackEvent();
  const dispatch = useAppDispatch();
  const tileId = useFixingTileId();
  const setIsModifyingField = useFixingSetIsModifyingField();
  const isModifyingField = useFixingIsModifyingField();

  const isStandardGroup = useAppSelector((state) => selectFixingIsStandardGroup(state, tileId));
  const clientWayData = useAppSelector((state) => selectClientWayFixingData(state, tileId));
  const notionalData = useAppSelector((state) => selectNotionalDataFixing(state, tileId));
  const notificationEmails = useAppSelector((state) => selectNotificationEmailsFixing(state, tileId));
  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const validate = useValidateFixing();
  const [submitFixing] = useSubmitFixingMutation({
    selectFromResult: () => ({}),
  });

  const marginType = useAppSelector((state) => selectFixingMarginType(state, tileId));

  const isChecking = useFixingIsChecking();

  const { areFieldsVisible, tile } = useAppSelector((state) => {
    const tile = selectFixingTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        areFieldsVisible: false,
      };
    }

    return {
      tile,
      areFieldsVisible: isDefined(tile.properties.currencyPair),
    };
  }, deepEqual);

  const handleSubmit = useCallback(() => {
    track('Order action', 'Submit Order');
    if (isNotDefined(tile)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    if (isNotDefined(clientId)) {
      console.error('[handleSubmit] client id is not defined');
      return;
    }

    if (isNotDefined(notificationEmails)) {
      console.error('[handleSubmit] Notification emails not found in the tile or user preferences.');
      return;
    }

    if (
      isNotDefined(tile.properties.currencyPair) ||
      isNotDefined(tile.properties.notional) ||
      isNotDefined(tile.properties.fixingDateUtc)
    ) {
      console.error('[handleSubmit] Currency pair is not defined in the tile.');
      return;
    }

    submitFixing({
      tileId,
      currency1: tile.properties.currencyPair.currency1,
      currency2: tile.properties.currencyPair.currency2,
      clientId,
      way: tile.properties.clientWay,
      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      fixingReference: tile.properties.fixingReference,
      fixingPriceType: tile.properties.isCcyForcedBidAsk ? 'BIDASK' : tile.properties.fixingPriceType,
      fixingDateUtc: tile.properties.fixingDateUtc,
      expiryDay: tile.properties.expiryDay,
      ...(tile.properties.isStandardGroup
        ? { fixingTime: tile.properties.fixingTime }
        : { fixingPlace: tile.properties.fixingPlace }),
      commercialMargin:
        tile.properties.fixingMarginType === 'MarginInBps' ? tile.properties.commercialMargin : undefined,
      fixingMarginType: tile.properties.fixingMarginType,
      notificationEmails: tile.properties.notificationEmails ?? [],
      clockOffset: tile.properties.clockOffset,
    })
      .unwrap()
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit take profit order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }, [tileId, clientId, notificationEmails, tile, submitFixing]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <OrderHeader orderType="FixingOrder" tile={tile} tileId={tileId} validate={validate} />

        {areFieldsVisible && (
          <>
            <div
              className="mx-16px"
              style={{
                display: 'grid',
                gap: '5px',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gridTemplateRows: '20px',
                alignItems: 'center',
              }}
            >
              <ConnectedFixingOrderSpot />
              <OrderClientWay clientWayData={clientWayData} validate={validate} />
              <OrderNotional
                notionalData={notionalData}
                errors={errors}
                validate={validate}
                setIsModifyingField={setIsModifyingField}
              />

              <FixingReference />
              <FixingPriceType />
              <FixingDate />
              <FixingDeliveryDate />
              {isStandardGroup ? <FixingTime /> : <FixingPlace />}
              <FixingMarginType />
              {marginType === 'MarginInBps' && (
                <IsInternalSales>
                  <FixingCommercialMargin />
                </IsInternalSales>
              )}

              <OrderNotifications
                tileId={tileId}
                errors={errors}
                notificationEmails={notificationEmails}
                validate={validate}
              />
            </div>

            <Spacer />

            <OrderFooter tile={tile} isModifyingField={isModifyingField} handleSubmit={handleSubmit} />
          </>
        )}
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});

function IsInternalSales({ children }: { children: React.ReactNode }) {
  const {
    currentUser: { userType },
  } = useUserContext();

  const isInternalSales = userType === 'Internal Sales';

  return isInternalSales ? children : null;
}
